import {
  Box,
  InputGroup,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  SystemStyleObject,
} from "@chakra-ui/react";
import { FieldError } from "react-hook-form/dist/types/errors";
import { ReactNode, forwardRef, useState, useRef, FC } from "react";

import { useLocalization } from "hooks/useLocalization";
import useClickOutside from "hooks/useClickOutside";
import Input, { InputTypes } from "components/Chakra/Input";
import { TranslationKeys } from "utils/localization";
import { OFFER_TYPE } from "@ud/config/prodeje/config";

const BaseInput = forwardRef<
  HTMLInputElement,
  {
    initialValue?: string;
    onChange: (value: string) => void;
    onFocus: (isFocused: boolean) => void;
    onEnter?: (value: string) => void;
    hasError: boolean;
    value?: string;
    type?: InputTypes;
    icon?: ReactNode;
    sx?: SystemStyleObject;
    placeholder?: string;
    isReadOnly?: boolean;
    flagInput?: ReactNode;
  }
>(
  (
    {
      onChange,
      onEnter,
      value,
      type,
      onFocus,
      hasError,
      icon,
      sx,
      placeholder,
      isReadOnly = false,
      flagInput,
    },
    ref,
  ) => {
    const handleSomeEvent = () => {
      onFocus(true);
    };

    return (
      <Box position="relative" w="100%">
        <Input
          isReadOnly={isReadOnly}
          sx={sx}
          id="select-search-input"
          type={type}
          ref={ref}
          value={value}
          bg="ud-white"
          _focus={{ boxShadow: "0" }}
          autoCorrect="off"
          autoComplete="off"
          fontWeight="bold"
          placeholder={placeholder}
          _placeholder={{ fontWeight: "normal" }}
          borderRadius="7px"
          rightElement={icon}
          handleFocus={handleSomeEvent}
          flagInput={flagInput}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // @ts-expect-error Ignore type
              onEnter && onEnter(e.target?.value || "");
            }
          }}
          onFocus={() => handleSomeEvent()}
          onChange={({ currentTarget: { value: val } }) => {
            onChange && onChange(val);
          }}
          borderColor={hasError ? "ud-primary" : "ud-grey-300"}
        />
      </Box>
    );
  },
);

type SelectType = {
  id: string;
  value: string;
};

type IProps = {
  name: string;
  onSelectItem: (item: SelectType) => void;
  width?: string;
  items: Array<{ label: string; value: string; id?: string }>;
  type?: InputTypes;
  noNegative?: boolean;
  extValue: string;
  error?: FieldError;
  customMessage?: ReactNode;
  customError?: TranslationKeys;
  icon?: ReactNode;
  sx?: SystemStyleObject;
  placeholder?: string;
  isReadOnly?: boolean;
  flagInput?: ReactNode;
  flagSelect?: ReactNode;
  selectedId?: string;
  dropdownMaxHeight?: string;
};

const SelectInput: FC<IProps> = ({
  items,
  width,
  onSelectItem,
  type,
  noNegative,
  extValue,
  error,
  customError,
  customMessage,
  isReadOnly,
  icon,
  sx,
  flagInput,
  flagSelect,
  placeholder,
  selectedId,
  dropdownMaxHeight = "200px",
}) => {
  const [isOpen, setOpen] = useState(false);
  const popperRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { translate } = useLocalization();

  const closeModal = (): void => {
    setOpen(false);
  };

  const handleFocusChange = (isFocused: boolean): void => {
    setOpen(isFocused);
  };

  useClickOutside(
    popperRef,
    () => {
      closeModal();
    },
    [inputRef],
  );

  const onSelect = (item: SelectType): void => {
    onSelectItem(item);
    closeModal();
  };

  return (
    <Popover
      isOpen={isOpen}
      autoFocus={false}
      closeOnBlur={false}
      placement="bottom-start"
      gutter={8}
      isLazy
      matchWidth
    >
      <PopoverTrigger>
        <Box w="100%">
          <InputGroup maxW={width}>
            <BaseInput
              sx={sx}
              type={type}
              ref={inputRef}
              value={extValue}
              onEnter={(value) => onSelect({ id: "0", value })}
              onChange={(inputValue) => {
                if (noNegative && inputValue && parseInt(inputValue)) {
                  onSelectItem({ id: "0", value: "0" });
                }
                onSelectItem({ id: "0", value: inputValue });
              }}
              onFocus={handleFocusChange}
              hasError={Boolean(error)}
              icon={icon}
              placeholder={placeholder}
              isReadOnly={isReadOnly}
              flagInput={
                OFFER_TYPE.SALE.apiId === selectedId?.toUpperCase()
                  ? flagInput
                  : undefined
              }
            />
          </InputGroup>
          {Boolean(error) && (
            <Text variant="form-error">
              {customError ? translate(customError) : error?.message}
            </Text>
          )}
          {customMessage && <Text variant="form-message">{customMessage}</Text>}
        </Box>
      </PopoverTrigger>
      <PopoverContent w="100%">
        <PopoverArrow bg="ud-white" borderColor="gray.200" />
        <PopoverBody
          p="0"
          ref={popperRef}
          _focus={{ boxShadow: "0" }}
          maxHeight={dropdownMaxHeight}
          overflowY="auto"
        >
          {items.map(({ label, value, id }, i) => (
            <Stack
              key={i}
              px="15px"
              py="10px"
              direction="row"
              align="center"
              justify="space-between"
              transition="all 333ms ease 0s"
              _hover={{ background: "ud-grey-100", cursor: "pointer" }}
              onClick={() => {
                id && onSelect({ id, value });
              }}
            >
              <Stack direction="row" align="center">
                <Text>{label}</Text>
              </Stack>
              {OFFER_TYPE.SALE.apiId === id?.toUpperCase() && <>{flagSelect}</>}
            </Stack>
          ))}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default SelectInput;
